// ── 상품 관리: 카테고리(상자) 머리줄 ─────────────────────────────────────────
// 검정 띠에 카테고리 이름 + (권한 있으면) 이름 고치기·버리기 버튼.
// 2026-09-17 사용자 확정: 상품 관리를 배정받은 **운영사 사람**도 카테고리를 만들고 고치고 버릴 수 있다.
//   서버도 같은 조건으로 막는다(운영사 소속 + 상품 관리/동적 관리 배정). 고객 테넌트는 종전처럼 자유 입력.
import React, { useState } from 'react';
import { SquarePen, Trash2 } from 'lucide-react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';

const BASE = '/api/admin/product-categories';

export function CategoryHeaderRow({ group, items = [], canManage, styles, onChanged }) {
  const { showToast } = useToast();
  const { catHead, catHeadTag, iconBtnIcon, deleteBtnIcon, input, addBtn } = styles;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  // 카테고리 id 가 있는 줄(운영사 전역 카테고리)만 고칠 수 있다. 테넌트의 자유 입력 분류는 id 가 없다.
  const editable = canManage && typeof group.key === 'number';

  // 기본상품 = 미팅지도 > 결과입력 > 계약완료 창에 자동으로 들어가는 상품.
  //   전에는 ⚙️ 설정 안에 있어서 찾기 어려웠다(2026-09-20 사용자 지적) → 머리줄로 꺼냈다.
  async function saveDefaultProduct(productId) {
    setSaving(true);
    const r = await apiClient.put(`${BASE}/${group.key}/default-product`, { product_id: productId || null });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '기본상품 저장 실패', 'error'); return; }
    showToast(productId ? '기본상품을 저장했습니다.' : '기본상품을 없앴습니다.', 'success');
    onChanged?.();
  }

  async function saveName() {
    const trimmed = name.trim();
    if (!trimmed) { showToast('카테고리명을 입력하세요.', 'error'); return; }
    setSaving(true);
    const r = await apiClient.put(`${BASE}/${group.key}`, { name: trimmed });
    setSaving(false);
    if (!r.ok) { showToast(r.data?.message || '이름 수정 실패', 'error'); return; }
    showToast('카테고리명을 바꿨습니다.', 'success');
    setEditing(false);
    onChanged?.();
  }

  async function remove() {
    setSaving(true);
    const r = await apiClient.delete(`${BASE}/${group.key}`);
    setSaving(false);
    setConfirming(false);
    if (!r.ok) { showToast(r.data?.message || '삭제 실패', 'error'); return; }
    showToast('카테고리를 삭제했습니다.', 'success');
    onChanged?.();
  }

  return (
    <div style={catHead}>
      {editing ? (
        <>
          <input autoFocus style={{ ...input, width: 160, fontSize: 12 }} value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditing(false); }} />
          <button style={{ ...addBtn, padding: '4px 10px', fontSize: 12 }} onClick={saveName} disabled={saving}>저장</button>
          <button style={{ ...iconBtnIcon, padding: '4px 8px', fontSize: 12 }} onClick={() => { setEditing(false); setName(group.name); }}>취소</button>
        </>
      ) : (
        <>
          {group.name}
          {group.tag && <span style={catHeadTag}>{group.tag}</span>}
          {editable && !confirming && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6 }}>
              <span style={catHeadTag} title="미팅지도에서 계약완료를 누르면 이 상품이 자동으로 들어갑니다">기본상품</span>
              <select
                style={{ ...input, padding: '3px 6px', fontSize: 11, maxWidth: 160, fontWeight: 400 }}
                value={group.defaultProductId ?? ''}
                disabled={saving}
                onChange={(e) => saveDefaultProduct(e.target.value ? Number(e.target.value) : null)}
                aria-label="기본상품"
              >
                <option value="">없음</option>
                {items.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </span>
          )}
          {editable && (
            <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {confirming ? (
                <>
                  <span style={{ fontSize: 11 }}>이 카테고리를 버릴까요?</span>
                  <button style={{ ...addBtn, padding: '4px 10px', fontSize: 12 }} onClick={remove} disabled={saving}>확인</button>
                  <button style={{ ...iconBtnIcon, padding: '4px 8px', fontSize: 12 }} onClick={() => setConfirming(false)}>취소</button>
                </>
              ) : (
                <>
                  <button style={iconBtnIcon} title="카테고리명 수정" aria-label="카테고리명 수정"
                    onClick={() => { setName(group.name); setEditing(true); }}>
                    <SquarePen size={14} />
                  </button>
                  <button style={deleteBtnIcon} title="카테고리 삭제" aria-label="카테고리 삭제"
                    onClick={() => setConfirming(true)}>
                    <Trash2 size={14} />
                  </button>
                </>
              )}
            </span>
          )}
        </>
      )}
    </div>
  );
}
